import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Input,
  Switch,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import IconCard from "../../static/icon/IconCard";
import { formatDateTime } from "../../utils/formatter";

const Promotion = ({ match: { params: { type } } }) => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [promotions, setPromotions] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentPromotion, setCurrentPromotion] = useState(null);

  useEffect(() => {
    async function getPromotions({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/promotion', window.location);
      url.searchParams.append('type', type);
      if (searchValue.code) url.searchParams.append('code', searchValue.code);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (typeof searchValue.internal === 'boolean') url.searchParams.append('internal', searchValue.internal);
      if (typeof searchValue.expose === 'boolean') url.searchParams.append('expose', searchValue.expose);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, promotions } = await res.json();
        setTotal(total);
        setPromotions(promotions);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPromotions(search);
  }, [search, accessToken, update, type]);

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Name",
      dataIndex: "nameEn",
      key: "nameEn",
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEns",
      key: "merchantNameEns",
      render: (merchantNameEns, promotion) => promotion.internal ? (
        <Tooltip title={merchantNameEns.join(', ')}>
          {merchantNameEns.length} merchant(s)
        </Tooltip>
      ) : 'Public Used',
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEns",
      key: "landlordNameEns",
      render: (landlordNameEns, promotion) => promotion.internal ? (
        <Tooltip title={landlordNameEns.join(', ')}>
          {landlordNameEns.length} landlord(s)
        </Tooltip>
      ) : 'Public Used',
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      render: (validFrom) => validFrom ? formatDateTime(new Date(validFrom)) : 'No Limit',
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      render: (validTo) => validTo ? formatDateTime(new Date(validTo)) : 'No Limit',
    },
    {
      title: "Discount Percent",
      dataIndex: "discountPercent",
      key: "discountPercent",
      render: (discountPercent) => `${discountPercent}%`,
      hidden: type !== 'discount',
    },
    {
      title: "Discount Baht",
      dataIndex: "discountBaht",
      key: "discountBaht",
      render: (discountBaht) => `${discountBaht} Baht`,
      hidden: type !== 'discount',
    },
    {
      title: "Green Point",
      dataIndex: "point",
      key: "point",
      render: (point) => `${point} Green Point`,
      hidden: type !== 'point',
    },
    {
      title: "Code Left",
      dataIndex: "code",
      key: "code",
      render: (_, promotion) => `${promotion.codeLeft} / ${promotion.codeCount}`,
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active, promotion) => (
        <Switch
          checked={active}
          onChange={async (active) => {
            const url = new URL('/api/promotion/' + promotion.id, window.location);

            const res = await fetch(url, {
              method: 'PATCH',
              headers: {
                'X-ACCESS-TOKEN': accessToken,
                'Content-type': 'application/json',
              },
              body: JSON.stringify({ active }),
            });
            if (res.ok) {
              notification.success({
                message: "Success",
                description: "Successfully saved data.",
              });
            } else {
              notification["error"]({
                message: "Something went wrong !",
                description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
              });
            }
            setUpdate(update + 1);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, promotion) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="View" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/promotion/${type}/${promotion.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
              twoToneColor="#00C9D6"
            />
          </Tooltip>

          {!promotion.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentPromotion(promotion)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ].filter(col => !col.hidden);

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconCard /> &nbsp;&nbsp;Promotion
            </span>
          }
        />

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            history.push({
              pathname: `/promotion/${type}/new`,
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            });
          }}
        >
          New Promotion
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Code"
                  name="code"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Code"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Cluster Name"
                  name="merchantIds"
                  colon={false}
                  initialValue={[]}
                >
                  <MerchantDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Area Type"
                  name="internal"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Area Type' },
                      { value: true, label: 'Selected Landlord' },
                      { value: false, label: 'Public Used' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Homepage"
                  name="expose"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Homepage' },
                      { value: true, label: 'Show in homepage' },
                      { value: false, label: 'Hide from public' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={14}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(promotion) => promotion.id}
          loading={loading}
          columns={columns}
          dataSource={promotions}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentPromotion}
        name="Promotion"
        type="promotion"
        editRecord={currentPromotion}
        onCancel={() => setCurrentPromotion(null)}
        onOk={() => {
          setCurrentPromotion(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default Promotion;
